import { Component, OnDestroy, OnInit } from '@angular/core';
import { trigger, transition, style, animate, query, stagger, state } from '@angular/animations';
import { ErrorManagerService, ManagedErrorInfo } from './error-manager.service';
import { DatePipe, NgClass, NgFor, NgIf } from "@angular/common";
import { MatIconModule } from '@angular/material/icon';
import { Subscription } from 'rxjs';

@Component({
    standalone: true,
    selector: 'app-error-manager',
    templateUrl: './error-manager.component.html',
    styleUrls: ['./error-manager.component.scss'],
    imports: [
        NgClass,
        NgFor,
        NgIf,
        DatePipe,
        MatIconModule
    ],
    animations: [
        trigger('toastIn', [
            transition(':enter', [
                style({ transform: 'translateY(-16px) scale(0.96)', opacity: 0 }),
                animate('260ms cubic-bezier(0.16, 1, 0.3, 1)', style({ transform: 'translateY(0) scale(1)', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('160ms ease-in', style({ transform: 'translateY(-12px) scale(0.96)', opacity: 0 }))
            ])
        ]),
        trigger('dialogIn', [
            transition(':enter', [
                style({ transform: 'translateY(20px) scale(0.97)', opacity: 0 }),
                animate('280ms cubic-bezier(0.16, 1, 0.3, 1)', style({ transform: 'translateY(0) scale(1)', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('180ms ease-in', style({ transform: 'translateY(12px) scale(0.98)', opacity: 0 }))
            ])
        ]),
        trigger('backdropIn', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('200ms ease-out', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('160ms ease-in', style({ opacity: 0 }))
            ])
        ]),
        trigger('listStagger', [
            transition(':enter', [
                query('.detail-item', [
                    style({ opacity: 0, transform: 'translateY(6px)' }),
                    stagger(40, [
                        animate('220ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
                    ])
                ], { optional: true })
            ])
        ])
    ]
})
export class ErrorManagerComponent implements OnInit, OnDestroy {
  notification: ManagedErrorInfo | null = null;
  minimized = false;
  private subscription?: Subscription;

  constructor(private errorManagerService: ErrorManagerService) {}

  ngOnInit() {
    this.subscription = this.errorManagerService.notification$.subscribe(notification => {
      this.notification = notification;
      this.minimized = false;
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  closeNotification() {
    this.errorManagerService.clear();
  }

  minimizeNotification(): void {
    this.minimized = true;
  }

  restoreNotification(): void {
    this.minimized = false;
  }

  hasDetails(): boolean {
    return this.detailCount() > 0;
  }

  detailCount(): number {
    return this.notification?.detailItems?.length ?? 0;
  }

  hasCorrections(): boolean {
    return this.detailCount() > 0;
  }

  getTitle(): string {
    if (!this.notification) return '';
    if (this.notification.type === 'error') return this.hasCorrections() ? 'Action requise' : 'Erreur';
    if (this.notification.type === 'warning') return 'Attention';
    if (this.notification.type === 'success') return 'Succès';
    return 'Information';
  }

  getSubtitle(): string {
    if (!this.notification) return '';
    if (this.hasCorrections()) {
      const count = this.detailCount();
      return `${count} élément${count > 1 ? 's' : ''} à corriger avant de continuer.`;
    }
    return '';
  }

  getCountLabel(): string {
    return String(this.detailCount()).padStart(2, '0');
  }

  formatField(field?: string): string {
    if (!field) return '';
    return field
      .replace(/\[(\d+)\]/g, ' [$1]')
      .replace(/\./g, ' › ')
      .trim();
  }

  getIcon(): string {
    if (!this.notification) return '';

    switch (this.notification.type) {
      case 'success':
        return 'check_circle';
      case 'error':
        return 'error_outline';
      case 'warning':
        return 'warning_amber';
      case 'info':
        return 'info';
      default:
        return '';
    }
  }

  trackDetail(index: number): number {
    return index;
  }

  getTypeClass(): string {
    if (!this.notification) return '';

    switch (this.notification.type) {
      case 'success':
        return 'tone-success';
      case 'error':
        return 'tone-error';
      case 'warning':
        return 'tone-warning';
      case 'info':
        return 'tone-info';
      default:
        return '';
    }
  }
}
